import React, { useContext, useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import UsersFilters from "./Filters";
import UsersTable from "./UsersTable";
import { difference, isEqual, omit } from "lodash";
import { debounce, map } from "lodash/fp";
import { Grid } from "@mui/material";
import { useLazyQuery } from "@apollo/client/react";
import CurrentUserContext from "@/Contexts/CurrentUser";
import { GetAllEmployeesDocument } from "@/graphql/generated/graphql";

dayjs.extend(relativeTime);

const DEFAULT_FILTERS = {
  search: "",
  tags: [] as string[],
};

const normalDebounce = debounce(500);
const mapId = map("id");

export type UsersFiltersType = typeof DEFAULT_FILTERS;

const UserManagement: React.FC = () => {
  const debounceFunc = useRef(normalDebounce(fn => fn())).current;
  const { currentUser } = useContext(CurrentUserContext);

  const [filters, setFilters] = useState<UsersFiltersType>(DEFAULT_FILTERS);

  const [getEmployees, { data, loading }] = useLazyQuery(GetAllEmployeesDocument);

  const getVariables = (currentFilters: UsersFiltersType) => {
    const normalizableParams = ["tags"];
    const baseParams = {
      first: 1000,
    };

    const filterParams = !difference(normalizableParams, Object.keys(currentFilters))
      ? currentFilters
      : { ...omit(currentFilters, normalizableParams), tagIds: mapId(currentFilters.tags) };

    return { ...baseParams, ...filterParams };
  };

  // 1. Initial Load
  useEffect(() => {
    if (currentUser?.defaultCompany?.id) {
      getEmployees({
        variables: getVariables(DEFAULT_FILTERS),
      });
    }
  }, [currentUser?.defaultCompany?.id]);

  const handleFilterChange = <K extends keyof UsersFiltersType>(key: K, value: UsersFiltersType[K]) => {
    setFilters((prev) => {
      const newState = { ...prev, [key]: value };

      debounceFunc.cancel();
      debounceFunc(() => {
        getEmployees({
          variables: getVariables(newState),
        });
      });

      return newState;
    });
  };

  const handleOnReset = () => {
    setFilters(DEFAULT_FILTERS);

    getEmployees({
      variables: getVariables(DEFAULT_FILTERS),
    });
  };

  return (
    <Grid container>
      <UsersFilters
        filters={filters}
        onChange={handleFilterChange}
        onReset={handleOnReset}
        disableReset={isEqual(filters, DEFAULT_FILTERS)}
      />

      <UsersTable
        loading={loading}
        data={data?.getEmployees}
      />
    </Grid>
  );
};

export default UserManagement;
